if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        // Inscreve no evento de atualização do carrinho para verificar limites
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.checkButtonLimit.bind(this));
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChangeLimit.bind(this));
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onVariantChangeLimit(event) {
        if (!event.data?.variant) return;
        
        const variant = event.data.variant;
        this.submitButton.dataset.variantId = variant.id;
        this.submitButton.dataset.maxQuantity = variant.metafields?.frkw?.['max-quantity-to-buy'] || '';
        this.submitButton.dataset.hasLimit = variant.metafields?.frkw?.['max-quantity-to-buy'] ? 'true' : 'false';
        
        // Revalida o botão após mudança de variante
        this.checkButtonLimit({ data: { cartData: null } });
      }

      async checkButtonLimit(event) {
        if (!this.submitButton) return;

        const variantId = parseInt(this.submitButton.dataset.variantId);
        const maxQty = parseInt(this.submitButton.dataset.maxQuantity);
        const hasLimit = this.submitButton.dataset.hasLimit === 'true';
        const isAvailable = this.submitButton.dataset.available === 'true';
        const price = parseFloat(this.submitButton.dataset.price);
        
        // Se produto indisponível ou sem preço, não fazer nada
        if (!isAvailable || !price || price <= 0) return;
        
        if (!hasLimit || !maxQty || isNaN(maxQty)) {
          // Produto sem limite, sempre habilitado se disponível
          return;
        }

        let cartData = event?.data?.cartData;
        
        if (!Array.isArray(cartData?.items)) {
          try {
            const response = await fetch('/cart.js');
            cartData = await response.json();
          } catch (error) {
            console.error('Error fetching cart:', error);
            return;
          }
        }

        const cartQty = (cartData.items || [])
          .filter((item) => Number(item.variant_id || item.id) === variantId)
          .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

        const isLimitReached = cartQty >= maxQty;
        
        const buttonTextSpan = this.submitButton.querySelector('.submit-button-text') || this.submitButtonText;
        
        if (isLimitReached) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (buttonTextSpan) {
            buttonTextSpan.textContent = 'Limite máximo atingido';
          }
        } else {
          // Remove o disabled apenas se o botão não estiver desabilitado por outros motivos
          if (this.submitButton.getAttribute('aria-disabled') !== 'true') {
            this.submitButton.removeAttribute('disabled');
            if (buttonTextSpan) {
              buttonTextSpan.textContent = window.variantStrings?.addToCart || 'Adicionar ao carrinho';
            }
          }
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const limitResult = await this.limitRequestedQuantity(formData);
        if (limitResult.blocked) {
          this.submitButton.setAttribute('disabled', 'disabled');
          const buttonTextSpan = this.submitButton.querySelector('.submit-button-text') || this.submitButtonText;
          if (buttonTextSpan) buttonTextSpan.textContent = 'Limite máximo atingido';
          this.handleErrorMessage(`O limite máximo de ${limitResult.maximum} unidade(s) já foi atingido.`);
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }
        if (limitResult.adjusted) {
          this.handleErrorMessage(
            `A quantidade foi ajustada para ${limitResult.quantity}, respeitando o limite máximo de ${limitResult.maximum} unidade(s).`
          );
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
                maximumQuantityAdjustments: limitResult.adjusted
                  ? [{ variantId: formData.get('id'), maximum: limitResult.maximum }]
                  : undefined,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                    this.showMaximumQuantityMessage(limitResult, formData.get('id'));
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
              this.showMaximumQuantityMessage(limitResult, formData.get('id'));
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      showMaximumQuantityMessage(limitResult, variantId) {
        if (!limitResult.adjusted) return;

        document.querySelectorAll(`input[data-quantity-variant-id="${variantId}"]`).forEach((input) => {
          const message = input.closest('quantity-popover')?.querySelector('[data-maximum-quantity-message]');
          if (!message) return;
          message.textContent = `A quantidade foi ajustada para o limite máximo de ${limitResult.maximum} unidade(s).`;
          message.classList.add('cart-item__maximum-quantity--adjusted');
        });
      }

      async limitRequestedQuantity(formData) {
        const maximum = parseInt(this.submitButton.dataset.maxQuantity);
        const variantId = parseInt(formData.get('id'));
        const requestedQuantity = parseInt(formData.get('quantity')) || 1;

        if (this.submitButton.dataset.hasLimit !== 'true' || Number.isNaN(maximum) || maximum <= 0) {
          return { blocked: false, adjusted: false };
        }

        try {
          const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
          const cartData = await response.json();
          const quantityInCart = (cartData.items || [])
            .filter((item) => Number(item.variant_id || item.id) === variantId)
            .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
          const availableQuantity = Math.max(maximum - quantityInCart, 0);

          if (availableQuantity === 0) return { blocked: true, adjusted: false, maximum };
          if (requestedQuantity > availableQuantity) {
            formData.set('quantity', availableQuantity);
            return { blocked: false, adjusted: true, quantity: availableQuantity, maximum };
          }
        } catch (error) {
          console.error('Não foi possível validar o limite máximo antes de adicionar o produto:', error);
        }

        return { blocked: false, adjusted: false };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
